import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { calculateChoiceTotals, formatVariableValue } from '@/lib/form-calculation'
import type { Choice, Question } from '@/types/form'
import { ImageIcon, Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { type EditableImageValue, ImageEditorDialog } from '../ImageEditorDialog'

type ChoiceBatchEditorProps = {
  question: Question
  label: string
  mode?: 'standard' | 'picture'
  onQuestionChange: (patch: Partial<Question>) => void
  onSettingsChange: (patch: Partial<Question['settings']>) => void
}

export function ChoiceBatchEditor({
  question,
  label,
  mode = 'standard',
  onQuestionChange,
  onSettingsChange,
}: ChoiceBatchEditorProps) {
  const autoValue = question.settings.choiceValueMode !== 'manual'
  const scoringEnabled = question.settings.enableChoiceScoring === true
  const [batchText, setBatchText] = useState('')
  const [defaultScore, setDefaultScore] = useState(
    Number(question.settings.defaultChoiceScore ?? 0),
  )
  const [defaultAmount, setDefaultAmount] = useState(
    Number(question.settings.defaultChoiceAmount ?? 0),
  )
  const [editingImageChoiceId, setEditingImageChoiceId] = useState<string | null>(null)
  const editingImageChoice = useMemo(
    () => question.choices.find((choice) => choice.id === editingImageChoiceId),
    [editingImageChoiceId, question.choices],
  )
  const scoringPreview = useMemo(() => {
    const answers = new Map([[question.id, question.choices.map((choice) => choice.value)]])
    return calculateChoiceTotals([question], answers)
  }, [question])

  function setChoices(choices: Choice[]) {
    onQuestionChange({
      choices: choices.map((choice, orderIndex) => ({ ...choice, orderIndex })),
    })
  }

  function updateChoice(id: string, patch: Partial<Choice>) {
    setChoices(
      question.choices.map((choice) => (choice.id === id ? { ...choice, ...patch } : choice)),
    )
  }

  function updateChoiceLabel(choice: Choice, label: string) {
    updateChoice(choice.id, {
      label,
      value: autoValue ? createChoiceValue(label, choice.orderIndex) : choice.value,
    })
  }

  function updateChoiceValue(choice: Choice, value: string) {
    updateChoice(choice.id, { value: normalizeManualValue(value, choice.orderIndex) })
  }

  function addChoice() {
    const orderIndex = question.choices.length
    const label = `選項 ${orderIndex + 1}`
    setChoices([
      ...question.choices,
      {
        id: crypto.randomUUID(),
        questionId: question.id,
        label,
        value: createChoiceValue(label, orderIndex),
        score: scoringEnabled ? defaultScore : undefined,
        amount: scoringEnabled ? defaultAmount : undefined,
        orderIndex,
      },
    ])
  }

  function applyBatchText() {
    const rows = parseChoiceRows(batchText)
    if (rows.length === 0) return
    setChoices(
      rows.map((row, orderIndex) => ({
        id: crypto.randomUUID(),
        questionId: question.id,
        label: row.label,
        value: autoValue
          ? createChoiceValue(row.label, orderIndex)
          : normalizeManualValue(row.value ?? row.label, orderIndex),
        score: scoringEnabled ? defaultScore : undefined,
        amount: scoringEnabled ? defaultAmount : undefined,
        orderIndex,
      })),
    )
    setBatchText('')
  }

  function toggleScoring(nextEnabled: boolean) {
    onSettingsChange({ enableChoiceScoring: nextEnabled })
    setChoices(
      question.choices.map((choice) => ({
        ...choice,
        score: nextEnabled ? Number(choice.score ?? defaultScore) : undefined,
        amount: nextEnabled ? Number(choice.amount ?? defaultAmount) : undefined,
      })),
    )
  }

  function applyDefaultScoring() {
    onSettingsChange({
      defaultChoiceScore: defaultScore,
      defaultChoiceAmount: defaultAmount,
      enableChoiceScoring: true,
    })
    setChoices(
      question.choices.map((choice) => ({
        ...choice,
        score: defaultScore,
        amount: defaultAmount,
      })),
    )
  }

  function toggleAutoValue(nextAutoValue: boolean) {
    onSettingsChange({ choiceValueMode: nextAutoValue ? 'auto' : 'manual' })
    if (!nextAutoValue) return
    setChoices(
      question.choices.map((choice, orderIndex) => ({
        ...choice,
        value: createChoiceValue(choice.label, orderIndex),
      })),
    )
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Label>{label}</Label>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            一行一個選項；可貼上多行文字批量建立，value 可自動產生或手動維護。
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <span>自動 value</span>
          <Switch
            aria-label="自動產生選項 value"
            checked={autoValue}
            onCheckedChange={toggleAutoValue}
          />
        </div>
      </div>

      <div className="rounded-md border border-indigo-100 bg-white p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-slate-950">選項分數 / 金額</div>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              用於邏輯測試器與結果頁分流的 mock-safe 計算，目前不寫入後端。
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span>啟用</span>
            <Switch
              aria-label="啟用選項分數與金額"
              checked={scoringEnabled}
              onCheckedChange={toggleScoring}
            />
          </div>
        </div>
        {scoringEnabled ? (
          <>
            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
              <div className="space-y-1">
                <Label className="text-xs">預設分數</Label>
                <Input
                  type="number"
                  value={defaultScore}
                  onChange={(event) => setDefaultScore(Number(event.currentTarget.value))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">預設金額</Label>
                <Input
                  type="number"
                  value={defaultAmount}
                  onChange={(event) => setDefaultAmount(Number(event.currentTarget.value))}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="self-end"
                onClick={applyDefaultScoring}
              >
                批次套用
              </Button>
            </div>
            <div className="mt-3 rounded-md bg-indigo-50 px-3 py-2 text-xs leading-5 text-indigo-800">
              全選預覽：總分 {scoringPreview.score}，金額{' '}
              {formatVariableValue(scoringPreview.amount, 'currency')}
            </div>
          </>
        ) : null}
      </div>

      <div className="grid gap-2">
        {question.choices.map((choice, index) => (
          <div key={choice.id} className="rounded-md border border-slate-200 bg-white p-2">
            {mode === 'picture' ? (
              <div className="grid min-h-[96px] grid-cols-[88px_minmax(0,1fr)_auto] items-center gap-3">
                <button
                  type="button"
                  onClick={() => setEditingImageChoiceId(choice.id)}
                  className="grid size-[88px] place-items-center overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50 text-xs text-slate-500 transition hover:border-indigo-300 hover:bg-indigo-50"
                  aria-label={`編輯「${choice.label}」圖片`}
                >
                  {choice.imageUrl ? (
                    <img
                      src={choice.imageUrl}
                      alt={choice.imageAlt || choice.label}
                      className="size-full"
                      style={{
                        objectFit: choice.imageFit ?? 'cover',
                        objectPosition: `${choice.imageFocalX ?? 50}% ${choice.imageFocalY ?? 50}%`,
                        transform: `scale(${choice.imageZoom ?? 1})`,
                        transformOrigin: `${choice.imageFocalX ?? 50}% ${choice.imageFocalY ?? 50}%`,
                      }}
                    />
                  ) : (
                    <span>
                      <ImageIcon className="mx-auto mb-1 size-5" />
                      上傳
                    </span>
                  )}
                </button>

                <div className="flex min-h-[88px] min-w-0 flex-col justify-center gap-2">
                  <Input
                    value={choice.label}
                    aria-label="選項標題"
                    onChange={(event) => updateChoiceLabel(choice, event.target.value)}
                  />
                  <ChoiceValueControl
                    value={choice.value}
                    autoValue={autoValue}
                    onChange={(value) => updateChoiceValue(choice, value)}
                  />
                  {scoringEnabled ? (
                    <ChoiceScoringControls
                      score={Number(choice.score ?? 0)}
                      amount={Number(choice.amount ?? 0)}
                      onChange={(patch) => updateChoice(choice.id, patch)}
                    />
                  ) : null}
                </div>

                <ChoiceRowActions
                  onEditImage={() => setEditingImageChoiceId(choice.id)}
                  onRemove={() =>
                    setChoices(question.choices.filter((item) => item.id !== choice.id))
                  }
                  showImageAction
                />
              </div>
            ) : (
              <div
                className={
                  scoringEnabled
                    ? 'grid grid-cols-[minmax(0,1fr)_minmax(110px,0.7fr)_minmax(150px,0.8fr)_auto] items-center gap-2'
                    : 'grid grid-cols-[minmax(0,1.2fr)_minmax(110px,0.8fr)_auto] items-center gap-2'
                }
              >
                <Input
                  value={choice.label}
                  aria-label={`${label} ${index + 1}`}
                  onChange={(event) => updateChoiceLabel(choice, event.target.value)}
                />
                <ChoiceValueControl
                  value={choice.value}
                  autoValue={autoValue}
                  onChange={(value) => updateChoiceValue(choice, value)}
                />
                {scoringEnabled ? (
                  <ChoiceScoringControls
                    compact
                    score={Number(choice.score ?? 0)}
                    amount={Number(choice.amount ?? 0)}
                    onChange={(patch) => updateChoice(choice.id, patch)}
                  />
                ) : null}
                <ChoiceRowActions
                  onRemove={() =>
                    setChoices(question.choices.filter((item) => item.id !== choice.id))
                  }
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addChoice}>
        <Plus className="size-4" />
        新增選項
      </Button>

      <div className="flex flex-col gap-2">
        <Label>批次貼上</Label>
        <Textarea
          value={batchText}
          onChange={(event) => setBatchText(event.currentTarget.value)}
          placeholder={'一行一個選項\n可用「選項文字, value」指定 value'}
          className="min-h-28 resize-y bg-white"
        />
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500">
            目前 {question.choices.length} 個選項；套用批次會取代現有選項。
          </span>
          <Button type="button" size="sm" onClick={applyBatchText} disabled={!batchText.trim()}>
            套用批次建立
          </Button>
        </div>
      </div>

      {editingImageChoice ? (
        <ImageEditorDialog
          open={Boolean(editingImageChoice)}
          onOpenChange={(open) => {
            if (!open) setEditingImageChoiceId(null)
          }}
          title={`編輯「${editingImageChoice.label}」圖片`}
          description="圖片只會保存在本機 mock preview，後端 R2 上傳會在後續批次接線。"
          uploadId={`picture-choice-${question.id}-${editingImageChoice.id}`}
          defaultAlt={editingImageChoice.label}
          value={choiceToEditableImage(editingImageChoice)}
          previewBackground="#F8FAFC"
          onChange={(image) => updateChoice(editingImageChoice.id, editableImageToChoice(image))}
          onRemove={() => {
            updateChoice(editingImageChoice.id, {
              imageUrl: undefined,
              imageAlt: undefined,
              imageFit: undefined,
              imageFocalX: undefined,
              imageFocalY: undefined,
              imageZoom: undefined,
              imageRadius: undefined,
            })
            setEditingImageChoiceId(null)
          }}
        />
      ) : null}
    </div>
  )
}

function ChoiceValueControl({
  value,
  autoValue,
  onChange,
}: {
  value: string
  autoValue: boolean
  onChange: (value: string) => void
}) {
  if (autoValue) {
    return (
      <div className="truncate rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-500">
        value: {value}
      </div>
    )
  }

  return (
    <Input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label="選項 value"
      className="font-mono text-xs"
    />
  )
}

function ChoiceScoringControls({
  score,
  amount,
  compact = false,
  onChange,
}: {
  score: number
  amount: number
  compact?: boolean
  onChange: (patch: Partial<Choice>) => void
}) {
  return (
    <div className={compact ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-2 gap-2'}>
      <Input
        type="number"
        value={score}
        onChange={(event) => onChange({ score: Number(event.currentTarget.value) })}
        aria-label="選項分數"
        placeholder="分數"
        className="text-xs"
      />
      <Input
        type="number"
        value={amount}
        onChange={(event) => onChange({ amount: Number(event.currentTarget.value) })}
        aria-label="選項金額"
        placeholder="金額"
        className="text-xs"
      />
    </div>
  )
}

function ChoiceRowActions({
  onRemove,
  onEditImage,
  showImageAction = false,
}: {
  onRemove: () => void
  onEditImage?: () => void
  showImageAction?: boolean
}) {
  return (
    <div className="flex shrink-0 items-center gap-1">
      {showImageAction ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onEditImage}
          aria-label="編輯圖片"
          title="編輯圖片"
        >
          <ImageIcon className="size-4" />
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label="刪除選項"
        title="刪除選項"
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  )
}

function parseChoiceRows(value: string) {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, manualValue] = line.split(/[\t,]/).map((part) => part.trim())
      return { label, value: manualValue }
    })
    .filter((row) => row.label)
}

function createChoiceValue(label: string, index: number) {
  const normalized = label
    .trim()
    .toLowerCase()
    .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
  return normalized || `option-${index + 1}`
}

function normalizeManualValue(value: string, index: number) {
  return value.trim() || `option-${index + 1}`
}

function choiceToEditableImage(choice: Choice): EditableImageValue {
  return {
    url: choice.imageUrl ?? '',
    alt: choice.imageAlt ?? choice.label,
    fit: choice.imageFit ?? 'cover',
    focalX: choice.imageFocalX ?? 50,
    focalY: choice.imageFocalY ?? 50,
    zoom: choice.imageZoom ?? 1,
    radius: choice.imageRadius ?? 12,
    height: 260,
  }
}

function editableImageToChoice(image: EditableImageValue): Partial<Choice> {
  return {
    imageUrl: image.url,
    imageAlt: image.alt,
    imageFit: image.fit,
    imageFocalX: image.focalX,
    imageFocalY: image.focalY,
    imageZoom: image.zoom,
    imageRadius: image.radius,
  }
}
